import { gql } from '@apollo/client';
import { Button, Card, Col, DatePicker, Form, Row, Select } from 'antd';
import { useQuery } from 'hooks/apollo';
import React, { useEffect, useMemo } from 'react';
import {
  PLANS,
} from 'shared/constants/urls';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { currencies, SEK } from 'shared/constants/currencies';
import { Module, LicenseInterval, DataSourceType } from 'shared/prisma/types';
import { dayjs } from 'shared/utils/date';
import { getValidLicenseInterval } from 'shared/utils/license';

import globalMessages from 'translations/messages';
import useUser from 'hooks/useUser';
import useFormatMessage from 'hooks/useFormatMessage';
import CurrencySelect from 'components/CurrencySelect';
import DashboardCard from 'components/DashboardCard';
import CurrencyFormat from 'components/CurrencyFormat';
import { DeleteFilled, ExportOutlined, PlusOutlined } from '@ant-design/icons';

const dataSourceModules = {
  [DataSourceType.Website]: [Module.Analytics, Module.UxSurveys, Module.Heatmaps],
  [DataSourceType.Cx]: [Module.CxSurveys],
};

const dataSourceIntervalOptions = {
  [DataSourceType.Website]: [LicenseInterval.Yearly, LicenseInterval.Monthly],
  [DataSourceType.Cx]: [LicenseInterval.Yearly],
};

const moduleDescriptions = {
  [Module.Analytics]: 'Intuitive analytics, yet as powerful as you need it to be. Measure traffic, behaviour, marketing, and conversions without intruding on your visitors\' privacy.',
  [Module.UxSurveys]: 'Understand who your users are, their intents, issues, and experiences. Get feedback on specific pages or functionality. High response rates and compliant around the world.',
  [Module.Heatmaps]: 'Make your visitors come to life and see things from their point of view. Optimise mobile and desktop experience and smoothen user journeys for higher conversion.',
};

const LICENSES = gql`
  query ActiveProLicenses($levels: [Int!], $modules: [Module!]) {
    activeModuleLicenses(levels: $levels, modules: $modules) {
      id
      title
      module
      level
      settings(keys: ["prices"])
    }
  }
`;

const ModuleLicenseCard = ({ module, license, value, onChange }) => {
  const visitLimit = Form.useWatch('visitLimit');
  const currency = Form.useWatch('currency');
  const interval = Form.useWatch('interval');
  const priceData = license?.settings.prices?.find(p => p.visitLimit === visitLimit);

  const actions = [
    <a href={PLANS} target="_blank">
      <Button type="link" icon={<ExportOutlined />}>View all features</Button>
    </a>,
    value
      ? <Button icon={<DeleteFilled />} onClick={() => onChange(false)} type="link" danger>Remove</Button>
      : <Button icon={<PlusOutlined />} onClick={() => onChange(true)} type="link">Add to order</Button>,
  ];

  let monthlyPrice = priceData?.monthlyPrice[currency];

  if (interval === LicenseInterval.Yearly && monthlyPrice) {
    monthlyPrice = Math.round(monthlyPrice * 0.8);
  }

  return (
    <DashboardCard title={<FormattedMessage {...globalMessages[module]} />} module={module} actions={actions}>
      <p>{moduleDescriptions[module]}</p>
      {priceData && currency ? (
        <h3 style={{ textAlign: 'right' }}>
          <CurrencyFormat value={monthlyPrice} currency={currency} />
          /month
        </h3>
      ) : (
        <Card.Meta title="No price data available" />
      )}
    </DashboardCard>
  );
};

const ModuleLicenseV2Form = ({ form, dataSource, onSubmit, onChange }) => {
  const $t = useFormatMessage();
  const { isSuperUser } = useUser();
  const availableModules = dataSourceModules[dataSource.type];
  const { data, loading } = useQuery(LICENSES, { variables: { levels: [4], modules: availableModules } });
  const startDate = Form.useWatch('startDate', form);
  const interval = Form.useWatch('interval', form);

  const analyticsLicense = data?.activeModuleLicenses.find(l => l.module === Module.Analytics);
  const limitOptions = useMemo(() => analyticsLicense?.settings.prices?.filter(p => p.visitLimit != null).map(p => ({ value: p.visitLimit, label: <FormattedNumber value={p.visitLimit} /> })), [analyticsLicense]);

  const getValues = (values) => {
    const licenses = data?.activeModuleLicenses.filter(l => values.modules[l.module]) || [];
    const modules = licenses.map(l => l.id);
    const isTrial = values.interval === LicenseInterval.Trial;
    const settings = [];

    for (const license of licenses) {
      const priceSettings = license?.settings.prices.find(p => p.visitLimit === values.visitLimit);
      if (priceSettings) {
        for (const key of Object.keys(priceSettings)) {
          if (!settings.find(s => s.key === key) && priceSettings[key] != null && key !== 'monthlyPrice') {
            settings.push({
              key,
              value: priceSettings[key],
            });
          }
        }
      }
    }

    return {
      ...values,
      modules,
      settings,
      licenses: data?.activeModuleLicenses,
      discount: !isTrial && modules.length === 3 ? 10 : 0,
    };
  };

  const onFinish = async (values) => {
    onSubmit(getValues(values));
  };

  const initialValues = {
    visitLimit: limitOptions?.[0]?.value,
    modules: availableModules.reduce((acc, module) => ({ ...acc, [module]: true }), {}),
    interval: getValidLicenseInterval({
      activeLicense: dataSource.activeLicense,
      startDate: dayjs(),
      interval: dataSource.activeLicense?.interval || LicenseInterval.Yearly,
    }),
    startDate: dayjs(),
    currency: SEK,
  };

  const onValuesChange = () => {
    onChange(getValues(form.getFieldsValue(true)));
  };

  useEffect(() => {
    form.resetFields();
    onValuesChange();
  }, [dataSource, data]);

  const intervalOptions = [...dataSourceIntervalOptions[dataSource.type]];

  if (isSuperUser) {
    intervalOptions.push(LicenseInterval.Trial);
  }

  return (
    <Form form={form} onFinish={onFinish} onValuesChange={onValuesChange} layout="vertical" initialValues={initialValues}>
      <Row gutter={[16, 16]}>
        <Col span={8}>
          <Form.Item name="visitLimit" label="Website visits/month" rules={[{ required: true, message: 'Required' }]}>
            <Select options={limitOptions} loading={loading} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Start date" name="startDate">
            <DatePicker allowClear={false} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="interval" label="Subscription">
            <Select options={intervalOptions.map(o => ({
              value: o,
              label: $t(globalMessages[o.toLowerCase()]),
            }))}
            />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="currency" label="Currency">
            <CurrencySelect currencies={currencies} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label="Products">
        <Row gutter={16}>
          {availableModules.map(module => (
            <Col span={24 / availableModules.length} key={module}>
              <Form.Item name={['modules', module]} noStyle>
                <ModuleLicenseCard module={module} license={data?.activeModuleLicenses.find(l => l.module === module)} />
              </Form.Item>
            </Col>
          ))}
        </Row>
      </Form.Item>
    </Form>
  );
};

export default ModuleLicenseV2Form;
